import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Button,
  Badge,
  Icon,
  Divider,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaPlus, FaRegClock, FaPen, FaPause, FaPlay } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { statut } from "variables/veille";
import { useVeilles } from "contexts/VeillesContext";

const FILTRES = [
  { id: "toutes", label: "Toutes" },
  { id: "active", label: "En cours" },
  { id: "programmee", label: "Programmées" },
  { id: "terminee", label: "Terminées" },
  { id: "en_pause", label: "En pause" },
];
const CANAL_COURT = { presse: "Presse", radio: "Radio", tele: "TV", reseaux: "Réseaux", web: "Web", juridique: "Juridique" };

export default function Veilles() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const [filtre, setFiltre] = useState("toutes");
  const { veilles, pauseVeille } = useVeilles();
  const recherche = (new URLSearchParams(location.search).get("q") || "").trim();
  const q = recherche.toLowerCase();

  const ordre = { active: 0, programmee: 1, en_pause: 2, terminee: 3, brouillon: 4 };
  const liste = useMemo(
    () =>
      veilles
        .filter((v) => filtre === "toutes" || v.statut === filtre)
        .filter((v) => !q || [v.nom, v.client, v.secteur, ...(v.motsCles || [])].join(" ").toLowerCase().includes(q))
        .sort((a, b) => (ordre[a.statut] ?? 99) - (ordre[b.statut] ?? 99)),
    [filtre, veilles, q]
  );
  const compteur = (id) => (id === "toutes" ? veilles.length : veilles.filter((v) => v.statut === id).length);

  function arreter(v) {
    const enPause = v.statut === "en_pause";
    pauseVeille(v.id);
    toast({
      title: enPause ? `Veille « ${v.client || v.nom} » relancée` : `Veille « ${v.client || v.nom} » mise en pause`,
      status: enPause ? "success" : "info",
      duration: 2500,
    });
  }
  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête : grand titre + barre teal + CTA */}
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} mb="24px" gap="14px">
        <Flex align="center">
          <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
          <Box>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color={textColor} lineHeight="1.1">Mes veilles</Text>
            <Text fontSize="md" color="gray.700" mt="4px">Retrouvez, filtrez et pilotez l'ensemble de vos veilles.</Text>
          </Box>
        </Flex>
        <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" size="lg" leftIcon={<Icon as={FaPlus} />} onClick={() => history.push("/admin/nouvelle-veille")}>
          Nouvelle veille
        </Button>
      </Flex>

      {veilles.length === 0 ? (
        <Card><CardBody><Flex direction="column" align="center" textAlign="center" w="100%" py="70px" px="20px">
          <Flex w="66px" h="66px" borderRadius="20px" bg="teal.50" align="center" justify="center" mb="18px"><Icon as={FaPlus} color="teal.400" fontSize="22px" /></Flex>
          <Text fontSize="xl" fontWeight="bold" color={textColor}>Aucune veille pour l'instant</Text>
          <Text fontSize="sm" color="gray.500" mt="6px" maxW="440px">Décrivez votre sujet, Martin cadre la veille pour vous et vous livre des rapports clairs, au rythme que vous choisissez.</Text>
          <Button mt="22px" bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaPlus} />} onClick={() => history.push("/admin/nouvelle-veille")}>Créer ma première veille</Button>
        </Flex></CardBody></Card>
      ) : (
      <>
      {/* Filtres */}
      <Flex wrap="wrap" gap="10px" mb="24px">
        {FILTRES.map((f) => {
          const on = filtre === f.id;
          return (
            <Button
              key={f.id}
              size="md"
              px="18px"
              borderRadius="full"
              onClick={() => setFiltre(f.id)}
              bg={on ? "teal.300" : "white"}
              color={on ? "white" : "gray.700"}
              border="1px solid"
              borderColor={on ? "teal.300" : "gray.200"}
              _hover={{ bg: on ? "teal.400" : "gray.50" }}
              fontWeight="bold"
            >
              {f.label}
              <Badge ml="8px" borderRadius="full" bg={on ? "whiteAlpha.400" : "gray.100"} color={on ? "white" : "gray.600"}>{compteur(f.id)}</Badge>
            </Button>
          );
        })}
      </Flex>

      {recherche && (
        <Flex align="center" justify="space-between" mb="16px" px="16px" py="12px" bg="teal.50" borderRadius="12px" gap="12px">
          <Text fontSize="sm" color="gray.700">Résultats pour « <Text as="span" fontWeight="bold" color="gray.800">{recherche}</Text> » · {liste.length} veille{liste.length > 1 ? "s" : ""}</Text>
          <Button size="sm" variant="ghost" color="teal.600" onClick={() => history.push("/admin/veilles")}>Effacer</Button>
        </Flex>
      )}

      {/* Rubrique grise englobant les cartes */}
      <Box bg="gray.100" borderRadius="24px" p={{ base: "16px", md: "24px" }}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacingX="24px" spacingY="24px">
          {liste.map((v) => {
            const initiales = (v.client || v.nom).split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
            const enPause = v.statut === "en_pause";
            const arretable = v.statut === "active" || v.statut === "en_pause";
            return (
              <Card key={v.id} transition="all .15s" _hover={{ transform: "translateY(-4px)", boxShadow: "0 22px 44px rgba(20,20,43,0.14)" }}>
                <CardBody p={{ base: "20px", md: "24px" }}>
                  <Flex direction="column" w="100%">
                    {/* Identité — cliquable vers le détail */}
                    <Flex align="center" mb="22px" cursor="pointer" onClick={() => history.push(`/admin/veilles/${v.id}`)}>
                      <Flex w="56px" h="56px" borderRadius="16px" bg="teal.50" color="teal.600" align="center" justify="center" fontWeight="bold" fontSize="lg" me="16px" flexShrink={0}>{initiales}</Flex>
                      <Box flex="1" minW="0">
                        <Text fontSize="lg" fontWeight="bold" color="gray.800" noOfLines={2} lineHeight="1.25">{v.nom}</Text>
                        <Text fontSize="sm" color="gray.500" noOfLines={1} mt="2px">{v.secteur}</Text>
                      </Box>
                      <Badge colorScheme={statut(v.statut).color} borderRadius="8px" flexShrink={0} ml="10px" px="10px" py="4px" fontSize="xs">{statut(v.statut).label}</Badge>
                    </Flex>

                    {/* Canaux + prochain rapport */}
                    <Flex justify="space-between" align="flex-end" gap="20px" wrap="wrap">
                      <Box minW="0">
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="0.5px" mb="10px">Canaux surveillés</Text>
                        <Flex gap="8px" wrap="wrap">
                          {v.canaux.map((c) => (
                            <Badge key={c} bg="gray.100" color="gray.700" borderRadius="8px" textTransform="none" fontWeight="medium" px="10px" py="4px" fontSize="xs">{CANAL_COURT[c] || c}</Badge>
                          ))}
                        </Flex>
                      </Box>
                      <Box textAlign="right" flexShrink={0}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="0.5px" mb="8px">Prochain rapport</Text>
                        <Flex align="center" justify="flex-end" gap="6px" color="gray.800">
                          <Icon as={FaRegClock} color="teal.500" fontSize="14px" />
                          <Text fontSize="md" fontWeight="bold" whiteSpace="nowrap">{v.prochaineDate}</Text>
                        </Flex>
                      </Box>
                    </Flex>

                    {/* Actions : modifier / arrêter */}
                    <Divider my="18px" borderColor="gray.100" />
                    <Flex gap="10px">
                      <Button
                        flex="1"
                        variant="outline"
                        borderColor="gray.200"
                        color="gray.700"
                        borderRadius="12px"
                        leftIcon={<Icon as={FaPen} fontSize="12px" />}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => history.push(`/admin/nouvelle-veille?edit=${v.id}`)}
                      >
                        Modifier
                      </Button>
                      <Button
                        flex="1"
                        variant="outline"
                        borderRadius="12px"
                        isDisabled={!arretable}
                        leftIcon={<Icon as={enPause ? FaPlay : FaPause} fontSize="12px" />}
                        borderColor={enPause ? "teal.200" : "red.200"}
                        color={enPause ? "teal.600" : "red.500"}
                        _hover={{ bg: enPause ? "teal.50" : "red.50" }}
                        onClick={() => arreter(v)}
                      >
                        {enPause ? "Reprendre" : "Arrêter"}
                      </Button>
                    </Flex>
                  </Flex>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>

        {liste.length === 0 ? (
          <Flex direction="column" align="center" textAlign="center" py="50px" px="16px">
            <Text fontWeight="bold" color={textColor}>Aucune veille trouvée</Text>
            <Text fontSize="sm" color="gray.500" mt="4px">Ajustez vos filtres ou créez une nouvelle veille.</Text>
          </Flex>
        ) : (
          <Flex mt="28px" px="4px" direction={{ base: "column", md: "row" }} align="center" justify="space-between" gap="16px">
            <Box textAlign={{ base: "center", md: "left" }}>
              <Text fontSize="md" fontWeight="bold" color="gray.800">Besoin de suivre un nouveau sujet ?</Text>
              <Text fontSize="sm" color="gray.700">Décrivez votre besoin, Martin cadre la veille pour vous.</Text>
            </Box>
            <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" leftIcon={<Icon as={FaPlus} />} onClick={() => history.push("/admin/nouvelle-veille")}>
              Créer une veille
            </Button>
          </Flex>
        )}
      </Box>
      </>
      )}
    </Flex>
  );
}
